import { Check, Loader2, RefreshCw } from "lucide-react";
import { FaTiktok, FaInstagram, FaTwitch } from "react-icons/fa";

/*
 * TrialOffer — 7-day trial card shown to signed-in users who haven't subscribed.
 * Presentational: `onStartTrial` should open the existing Stripe checkout.
 * The trial is never unlocked by this component; checkout + backend verification handle that.
 */

const PURPLE = "#9146FF";
const MINT = "#84ff00";
const BG = "#07060d";
const HAIR = "rgba(255,255,255,0.08)";
const SURF = "rgba(255,255,255,0.035)";

const PLATFORMS = [
  { id: "tiktok", label: "TikTok", color: "#ff2d6f", Icon: FaTiktok },
  { id: "instagram", label: "Instagram", color: "#e1306c", Icon: FaInstagram },
  { id: "twitch", label: "Twitch", color: PURPLE, Icon: FaTwitch },
];

const BENEFITS = [
  "Compare the signals behind every post",
  "Spot formats that can earn more views",
  "All available tracker features included",
];

export function TrialOffer({ onStartTrial, busy = false, error = "", configured = true }) {
  const start = () => { if (!busy && configured) onStartTrial(); };
  return (
    <section
      aria-label="Tracker subscription"
      className="relative mx-auto w-full my-8 max-w-md overflow-hidden rounded-3xl border px-6 py-10 text-center sm:px-10 sm:py-12"
      style={{
        borderColor: HAIR,
        background: `radial-gradient(80% 60% at 50% 0%, ${PURPLE}26, transparent 70%), ${SURF}`,
      }}
    >
      {/* Label */}
      <p className="text-[11px] font-semibold uppercase tracking-[0.18em]" style={{ color: MINT }}>
        All three platforms. One place.
      </p>

      {/* Heading */}
      <h2 className="mt-3 text-2xl font-semibold tracking-[-0.02em] text-white sm:text-3xl">
        Find what works. Grow what’s next.
      </h2>
      <p className="mx-auto mt-3 max-w-sm text-sm leading-relaxed text-white/60">
        Compare your TikTok, Instagram Reels, and Twitch performance signals to plan content built to grow views.
      </p>

      {/* Platform logos */}
      <div className="mt-6 flex items-center justify-center gap-3">
        {PLATFORMS.map((p) => (
          <span
            key={p.id}
            title={p.label}
            role="img"
            aria-label={p.label}
            className="grid h-10 w-10 place-items-center rounded-xl border text-xs font-bold"
            style={{ borderColor: `${p.color}55`, backgroundColor: `${p.color}1a`, color: p.color }}
          >
            <p.Icon aria-hidden="true" className="h-5 w-5" />
          </span>
        ))}
      </div>

      {/* Offer */}
      <div className="mt-8">
        <p className="text-4xl font-semibold tracking-[-0.03em] text-white">7 days free</p>
        <p className="mt-1.5 text-sm text-white/55">
          Then <span className="font-semibold text-white/85">$10 USD/month</span>
        </p>
      </div>

      {/* Benefits */}
      <ul className="mx-auto mt-7 max-w-xs space-y-2.5 text-left">
        {BENEFITS.map((b) => (
          <li key={b} className="flex items-center gap-2.5 text-sm text-white/80">
            <span
              className="grid h-5 w-5 shrink-0 place-items-center rounded-full"
              style={{ backgroundColor: `${MINT}1f` }}
            >
              <Check className="h-3 w-3" style={{ color: MINT }} />
            </span>
            {b}
          </li>
        ))}
      </ul>

      {/* Primary action */}
      <button
        type="button"
        onClick={start}
        disabled={busy || !configured}
        className="focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-300 focus-visible:ring-offset-4 focus-visible:ring-offset-[#07060d] mt-8 inline-flex min-h-12 w-full items-center justify-center gap-2 rounded-full px-6 text-sm font-semibold text-white transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-70"
        style={{ backgroundColor: PURPLE, boxShadow: `0 10px 40px -12px ${PURPLE}, inset 0 1px 0 rgba(255,255,255,0.2)` }}
      >
        {busy && <Loader2 className="h-4 w-4 animate-spin" />}
        {busy ? "Opening secure checkout…" : "Start my 7-day free trial"}
      </button>

      {/* Inline error with retry */}
      {error && (
        <div
          role="alert"
          className="mt-4 flex items-center justify-between gap-3 rounded-xl border px-4 py-3 text-left"
          style={{ borderColor: "rgba(255,80,80,0.35)", backgroundColor: "rgba(255,80,80,0.08)" }}
        >
          <p className="text-xs leading-relaxed text-red-200">
            {error}
          </p>
          <button
            type="button"
            onClick={start}
            disabled={busy || !configured}
            className="inline-flex shrink-0 items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-white/5"
            style={{ borderColor: HAIR }}
          >
            <RefreshCw className="h-3 w-3" /> Retry
          </button>
        </div>
      )}

      {!configured && <p className="mt-3 text-xs text-white/50">Checkout isn’t ready yet. Please try again later.</p>}

      {/* Billing disclosure — always visible, including mobile */}
      <p className="mt-4 text-xs leading-relaxed text-white/70">
        Card required. Your card will automatically be charged $10 USD/month after 7 days unless you cancel before
        your trial ends.
      </p>
      <p className="mt-2 text-[11px] leading-relaxed text-white/45">
        Cancel anytime. Keep access through your trial or paid period. We’ll email you a reminder 3 days before your
        trial ends.
      </p>

      {/* Footer note */}
      <p className="mt-6 border-t pt-4 text-[11px] text-white/35" style={{ borderColor: HAIR }}>
        Available metrics vary by platform. Instagram views are currently estimates.
      </p>
    </section>
  );
}

export { BG as TRIAL_BG };
